import {HttpClient, HttpHeaders, HttpResponse} from "@angular/common/http";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {User} from "../../authentication/authentication.service";
import {AccountRequest} from "./request-bodies";
import {Permission} from "../../app-settings-panel/user-management/user-management.component";
import {TokenTypeEnum} from "../../token-type.enum";

export class UserEndpoints {

	constructor(private http: HttpClient) {
	}

	resendActivationEmail(userEmail: string): Observable<{ activationEmailResendDate: Date, accountAlreadyActive: boolean }> {
		const url: string = `${environment.apiUrl}/account/resend-activation-email?email=${encodeURIComponent(userEmail)}`;
		return this.http.post<{ activationEmailResendDate: Date, accountAlreadyActive: boolean }>(url, null);
	}

	changeEmail(from: string, to: string): Observable<any> {
		const url: string = `${environment.apiUrl}/account/${from}/change-email/${to}`;
		return this.http.post<void>(url, null, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	changePassword(currentPassword: string, newPassword: string): Observable<any> {
		const url: string = `${environment.apiUrl}/account/change-password`;
		const body = {currentPassword, newPassword};
		return this.http.post<void>(url, body, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	getUser(email: string): Observable<User> {
		return this.http.get<User>(`${environment.apiUrl}/account/${email}`);
	}

	createUserAccount(request: AccountRequest): Observable<any> {
		return this.http.post(`${environment.apiUrl}/app-settings/account/create`, request, {headers: {"Content-Type": "application/json;charset=UTF-8"}});
	}

	deleteUserAccount(email: string): Observable<any> {
		return this.http.delete(`${environment.apiUrl}/app-settings/account/${email}`);
	}

	modifyUserAccount(request: AccountRequest): Observable<any> {
		return this.http.post(`${environment.apiUrl}/app-settings/account/modify`, request, {headers: {"Content-Type": "application/json;charset=UTF-8"}});
	}

	getAllPossiblePermissions(): Observable<Permission[]> {
		return this.http.get<Permission[]>(`${environment.apiUrl}/app-settings/account/possible-permissions`);
	}

	getTechnicalReviewers(): Observable<User[]> {
		const url: string = `${environment.apiUrl}/account/technical-reviewers`;
		return this.http.get<User[]>(url, {observe: "body", responseType: "json"});
	}

	getApplicationReviewers(): Observable<User[]> {
		const url: string = `${environment.apiUrl}/account/application-reviewers`;
		return this.http.get<User[]>(url, {responseType: "json"})
	}

	resetPassword(email: string): Observable<any> {
		const url: string = `${environment.apiUrl}/account/${encodeURIComponent(email.toLowerCase())}/send-reset-password-email`;
		return this.http.post<any>(url, null, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})})
	}

	getNonce(email: string): Observable<string> {
		const url: string = `${environment.apiUrl}/user/${encodeURIComponent(email)}/nonce`;
		return this.http.get(url, {responseType: "text"});
	}

	verifyToken(email: string, token: string, tokenType: TokenTypeEnum): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/account/verify-token?token=${token}&email=${encodeURIComponent(email)}&type=${tokenType}`;
		return this.http.get(url, {observe: "response"});
	}

	verifyChangeProposalToken(email: string, token: string): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/account/verify-change-proposal-token?token=${token}&email=${encodeURIComponent(email)}`;
		return this.http.get<HttpResponse<any>>(url, {observe: "response", responseType: "json"});
	}

	verifyTransferDeclinedToken(email: string, token: string): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/account/verify-transfer-declined-token?token=${token}&email=${encodeURIComponent(email)}`;
		return this.http.get<HttpResponse<any>>(url, {observe: "response", responseType: "json"});
	}

	activateAccount(email: string, password: string): Observable<any> {
		const url: string = `${environment.apiUrl}/account/activate`;
		return this.http.post(url, {email, password}, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	resetPasswordFromProvidedUrl(email: string, password: string, token: string): Observable<any> {
		const url: string = `${environment.apiUrl}/account/reset-password`;
		return this.http.post(url, {email, password, token}, {headers: new HttpHeaders({"Content-Type": "application/json;charset=UTF-8"})});
	}

	switchCurrentRole(newRole: string): Observable<HttpResponse<any>> {
		return this.http.post(`${environment.apiUrl}/account/switch-current-role?to=${newRole}`, null, {observe: "response"});
	}

	authenticate(username: string, password: string, nonce: string, cnonce: string): Observable<HttpResponse<any>> {
		const url: string = `${environment.apiUrl}/user/authenticate`;
		return this.http.post<HttpResponse<any>>(url, {username, password, nonce, cnonce}, {observe: "response"});
	}

	getCertOfficials(): Observable<User[]> {
		const url: string = `${environment.apiUrl}/account/cert-officials`;
		return this.http.get<User[]>(url, {observe: "body", responseType: "json"})
	}

	checkAccountExists(email: string): Observable<string> {
		const url: string = `${environment.apiUrl}/account/${encodeURIComponent(email)}/check-account-exist`;
		return this.http.get(url, {observe: "body", responseType: "text"})
	}

	reevaluatePermissions() {
		const url: string = `${environment.apiUrl}/account/reevaluate-permissions`;
		return this.http.post(url, null);
	}
}
